import React, { useEffect, useMemo, useState } from 'react';
import { applyTransforms, headerStats } from '../utils/seriesTransforms';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ReferenceLine, CartesianGrid,
} from 'recharts';
import './FundamentalsChart.css';
import { setChartView } from '../utils/chartView';

/**
 * FundamentalsChart — price-over-fundamentals overlay (FAST Graphs lineage).
 * Price is the line; the fundamental (Revenue / Free Cash Flow / diluted EPS) is
 * drawn as EDGE-TO-EDGE bars — one bar per time bucket (day/week/month depending
 * on the timeframe), each held at the fundamental value in effect on that date
 * (stepping when new earnings land) — so the backdrop fills densely behind the
 * price line. Data: GET /api/public/history/{ticker}?range=... (public, cached).
 */
const METRICS = [
  { key: 'revenue', label: 'Revenue', color: '#00D4AA' },
  { key: 'fcf', label: 'Free cash flow', color: '#6495ED' },
  { key: 'eps', label: 'Earnings (EPS)', color: '#A78BFA' },
];
// Transforms over the series, borrowed in spirit from how VJN presents
// financials: a few transforms over ONE dataset reads as far more depth than a
// pile of separate charts, and costs almost nothing because the data is already
// here. Growth is the one that earns its keep — "revenue went up" and "growth is
// decelerating" are different conclusions from identical numbers.
const GROWTHS = [
  { key: 'off', label: 'Level' },
  { key: 'yoy', label: 'YoY' },
  { key: 'qoq', label: 'QoQ' },
];
const SMOOTHS = [0, 2, 4];
const RANGES = [
  { key: '1w', label: '1W' },
  { key: '1m', label: '1M' },
  { key: '3m', label: '3M' },
  { key: 'ytd', label: 'YTD' },
  { key: '1y', label: '1Y' },
  { key: '5y', label: '5Y' },
];

function fmtMoney(v, perShare) {
  if (v == null || !isFinite(v)) return '—';
  if (perShare) return `$${v.toFixed(2)}`;
  const a = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (a >= 1e12) return `${sign}$${(a / 1e12).toFixed(2)}T`;
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(0)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(0)}K`;
  return `${sign}$${a.toFixed(0)}`;
}

export default function FundamentalsChart({ ticker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [metric, setMetric] = useState('revenue');
  const [range, setRange] = useState('1y');
  const [growth, setGrowth] = useState('off');
  const [smoothN, setSmoothN] = useState(0);
  const [quarter, setQuarter] = useState(0);   // 0 = all, 1..4 = fiscal slot

  // Tell the share picker which view is on screen, so "This chart" shares what
  // the user is actually looking at. See utils/chartView.js.
  useEffect(() => {
    setChartView(ticker, { metric, range });
  }, [ticker, metric, range]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    fetch(`/api/public/history/${encodeURIComponent(ticker)}?range=${range}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('no history'))))
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setFailed(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ticker, range]);

  const raw = useMemo(() => (data && Array.isArray(data.series) ? data.series : []), [data]);

  // Stats come from the RAW series, never the transformed one: a trailing CAGR
  // computed off smoothed or growth-converted numbers would be a statistic about
  // a statistic, and wrong.
  const stats = useMemo(() => headerStats(raw), [raw]);

  const rows = useMemo(
    () => applyTransforms(raw, metric, { growth, smoothN, quarter }),
    [raw, metric, growth, smoothN, quarter],
  );

  // The quarter filter only means something on a quarterly series, which the
  // API gives for the long ranges. Offering it on a 1-week window would be a
  // control that silently does nothing.
  const quarterly = raw.length >= 8 && (range === '5y' || range === '1y');

  if (loading) return <div className="fchart"><div className="fchart-note">Loading financials…</div></div>;
  if (failed || !data) return null;

  const active = METRICS.find((m) => m.key === metric) || METRICS[0];
  const perShare = metric === 'eps';
  const fairValue = data.fair_value;
  const bucket = data.bucket;

  if (!rows.length || !rows.some((r) => r[metric] != null)) {
    return (
      <div className="fchart">
        <ChartHead {...{ active, metric, setMetric, range, setRange, growth, setGrowth,
                         smoothN, setSmoothN, quarter, setQuarter, quarterly, stats }} />
        <div className="fchart-note">No financials for this timeframe — try a wider range.</div>
      </div>
    );
  }

  // Fill every bar from the BOTTOM of the metric's own range (not from 0), so
  // Revenue / FCF / EPS all render as a dense filled backdrop no matter the sign
  // or scale — negatives no longer hang down and outliers don't crush the rest
  // into a sliver. The price line carries the trend; the bars are the fundamental
  // context. Explicit numbers per metric so recharts can't cache the wrong scale.
  const barVals = rows.map((r) => Number(r[metric])).filter((v) => Number.isFinite(v));
  let lo = barVals.length ? Math.min(...barVals) : 0;
  let hi = barVals.length ? Math.max(...barVals) : 1;
  if (!(hi > lo)) hi = lo + (Math.abs(lo) || 1) * 0.1;
  const _span = hi - lo || 1;
  const leftMin = lo - _span * 0.06;  // slightly below the min so the shortest bar still shows
  const leftMax = hi + _span * 0.04;

  const fmtTick = (label) => {
    const d = new Date(label);
    if (isNaN(d.getTime())) return label;
    if (bucket === 'day') return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
    return `${d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })} '${String(d.getUTCFullYear()).slice(2)}`;
  };
  // ~6 evenly spaced axis labels regardless of how many bars there are.
  const tickInterval = Math.max(0, Math.floor(rows.length / 6) - 1);

  return (
    <div className="fchart">
      <ChartHead {...{ active, metric, setMetric, range, setRange, growth, setGrowth,
                         smoothN, setSmoothN, quarter, setQuarter, quarterly, stats }} />

      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart key={`${range}:${metric}`} data={rows} margin={{ top: 8, right: 6, left: 4, bottom: 0 }} barCategoryGap={0} barGap={0}>
          <CartesianGrid stroke="#1E293B" vertical={false} />
          <XAxis
            dataKey="label"
            tickFormatter={fmtTick}
            interval={tickInterval}
            tick={{ fill: '#94A3B8', fontSize: 10 }}
            minTickGap={16}
          />
          <YAxis yAxisId="left" tick={{ fill: '#94A3B8', fontSize: 10 }} width={46}
            domain={[leftMin, leftMax]} allowDataOverflow
            tickFormatter={(v) => fmtMoney(v, perShare)} />
          <YAxis yAxisId="right" orientation="right" tick={{ fill: '#94A3B8', fontSize: 10 }} width={44}
            domain={['auto', 'auto']}
            tickFormatter={(v) => `$${v >= 1000 ? Math.round(v).toLocaleString() : v.toFixed(0)}`} />
          <Tooltip
            contentStyle={{ background: '#1C2640', border: '1px solid #334155', borderRadius: 8, color: '#F1F5F9', fontSize: '0.76rem' }}
            labelStyle={{ color: '#94A3B8' }}
            labelFormatter={(label) => {
              const d = new Date(label);
              return isNaN(d.getTime()) ? label
                : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
            }}
            formatter={(v, n) => {
              if (n === 'price') return [`$${Number(v).toFixed(2)}`, 'Price'];
              return [fmtMoney(v, perShare), active.label];
            }}
          />
          {fairValue != null && (
            <ReferenceLine yAxisId="right" y={fairValue} stroke="#FBBF24" strokeDasharray="5 4"
              label={{ value: `Fair value $${Number(fairValue).toFixed(0)}`, position: 'insideTopRight', fill: '#FBBF24', fontSize: 10 }} />
          )}
          <Bar key={metric} yAxisId="left" dataKey={metric} fill={active.color} fillOpacity={0.82}
            baseValue={leftMin} isAnimationActive={false} />
          <Line yAxisId="right" type="monotone" dataKey="price" stroke="#F1F5F9" strokeWidth={2}
            dot={false} connectNulls isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>

      <div className="fchart-legend">
        <span className="fchart-key"><i style={{ background: active.color }} /> {active.label} (bars)</span>
        <span className="fchart-key"><i className="line" style={{ background: '#F1F5F9' }} /> Price (line)</span>
        {fairValue != null && <span className="fchart-key"><i className="dash" /> DCF fair value</span>}
      </div>
      <div className="fchart-foot">
        Bars hold each quarter's {active.label.toLowerCase()} and step when new earnings land; the line is price.
        Where price runs far above the bars{fairValue != null ? ' or the fair-value line' : ''}, the stock is getting expensive.
      </div>
    </div>
  );
}

function StatStrip({ stats }) {
  if (!stats) return null;
  const pct = (v) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(0)}%`);
  const tone = (v) => (v == null ? '' : v >= 0 ? ' is-up' : ' is-down');
  return (
    <div className="fchart-stats">
      {/* TRAILING only, and it says so. VJN's equivalent leans on forward
          estimates; there is no analyst-estimate source wired up here and a
          made-up forward number is worse than none. */}
      <span className="fchart-stats-label">Trailing growth</span>
      {[1, 2, 3, 5].map((y) => (
        <span key={y} className="fchart-stat">
          <b>{y}Y</b>
          <span className={`fchart-stat-v${tone(stats.growth[y]?.revenue)}`}>
            rev {pct(stats.growth[y]?.revenue)}
          </span>
          <span className={`fchart-stat-v${tone(stats.growth[y]?.eps)}`}>
            eps {pct(stats.growth[y]?.eps)}
          </span>
        </span>
      ))}
      {stats.pe != null && (
        <span className="fchart-stat"><b>P/E</b>
          <span className="fchart-stat-v">{stats.pe.toFixed(1)} ttm</span>
        </span>
      )}
    </div>
  );
}

function ChartHead({ active, metric, setMetric, range, setRange, growth, setGrowth,
                     smoothN, setSmoothN, quarter, setQuarter, quarterly, stats }) {
  return (
    <div className="fchart-head">
      <span className="fchart-title">Price vs. {active.label.toLowerCase()}</span>
      <div className="fchart-controls">
        <div className="fchart-toggle" role="tablist" aria-label="Chart metric">
          {METRICS.map((m) => (
            <button key={m.key} role="tab" aria-selected={metric === m.key}
              className={`fchart-tab${metric === m.key ? ' is-on' : ''}`} onClick={() => setMetric(m.key)}>
              {m.label}
            </button>
          ))}
        </div>
        <div className="fchart-toggle fchart-range" role="tablist" aria-label="Timeframe">
          {RANGES.map((r) => (
            <button key={r.key} role="tab" aria-selected={range === r.key}
              className={`fchart-tab${range === r.key ? ' is-on' : ''}`} onClick={() => setRange(r.key)}>
              {r.label}
            </button>
          ))}
        </div>

        {/* Transforms. Separate row so they read as "how the series is
            expressed" rather than as more metrics or more timeframes. */}
        <div className="fchart-toggle" role="tablist" aria-label="Show as">
          {GROWTHS.map((g) => (
            <button key={g.key} role="tab" aria-selected={growth === g.key}
              title={g.key === 'off' ? 'Show the level' : `Show ${g.label} growth`}
              className={`fchart-tab${growth === g.key ? ' is-on' : ''}`}
              onClick={() => setGrowth(g.key)}>
              {g.label}
            </button>
          ))}
        </div>

        <div className="fchart-toggle" role="group" aria-label="Smoothing">
          {SMOOTHS.map((n) => (
            <button key={n} type="button"
              title={n ? `${n}-period average` : 'No smoothing'}
              className={`fchart-tab${smoothN === n ? ' is-on' : ''}`}
              onClick={() => setSmoothN(n)}>
              {n ? `~${n}` : 'Raw'}
            </button>
          ))}
        </div>

        {quarterly && (
          <div className="fchart-toggle" role="group" aria-label="Fiscal quarter">
            {[0, 1, 2, 3, 4].map((q) => (
              <button key={q} type="button"
                title={q ? `Only fiscal Q${q}, to isolate seasonality` : 'All quarters'}
                className={`fchart-tab${quarter === q ? ' is-on' : ''}`}
                onClick={() => setQuarter(q)}>
                {q ? `FQ${q}` : 'All'}
              </button>
            ))}
          </div>
        )}
      </div>
      <StatStrip stats={stats} />
    </div>
  );
}
